import { useState, useEffect, useCallback } from 'react';
import { EODSubmission, EODFormData } from '@/types/eod';
import { supabase } from '@/integrations/supabase/client';
import { format } from 'date-fns';

const DRAFT_KEY = 'venture_eod_draft';
const LAST_PROMPT_KEY = 'venture_eod_last_prompt';

export function useEODForm() {
  const [isOpen, setIsOpen] = useState(false);
  const [submissions, setSubmissions] = useState<EODSubmission[]>([]);
  const [todaySubmission, setTodaySubmission] = useState<EODSubmission | null>(null);
  const [isEditing, setIsEditing] = useState(false);

  // Load submissions from Supabase
  useEffect(() => {
    fetchSubmissions();
  }, []);

  const fetchSubmissions = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.user?.id) return;

    const { data, error } = await supabase
      .from('eod_reports')
      .select('*')
      .eq('user_id', session.user.id)
      .order('report_date', { ascending: false });

    if (error) {
      console.error('Error fetching EOD submissions:', error);
      return;
    }

    const mapped: EODSubmission[] = (data || []).map((row: any) => ({
      id: row.id,
      user_id: row.user_id,
      date: row.report_date,
      demos_booked: row.demos_booked,
      demos_showed: row.demos_showed,
      demos_closed: row.demos_closed,
      revenue_closed: row.revenue_closed ? row.revenue_closed / 100 : undefined, // cents to dollars
      show_rate: row.show_rate,
      lead_quality: row.lead_quality,
      lead_quality_issues: row.lead_quality_issues || '',
      issues: row.issues || '',
      notes: row.notes || '',
      tomorrow_focus: row.tomorrow_focus || '',
      submitted_at: row.submitted_at,
      submitted_late: row.submitted_late,
    }));

    setSubmissions(mapped);

    // Check if today's submission exists
    const today = format(new Date(), 'yyyy-MM-dd');
    const existing = mapped.find(s => s.date === today);
    if (existing) {
      setTodaySubmission(existing);
    }
  };

  // Check if should show EOD prompt (7pm PT logic)
  useEffect(() => {
    const checkTime = () => {
      const now = new Date();
      // Convert to PT (Pacific Time)
      const ptTime = new Date(now.toLocaleString('en-US', { timeZone: 'America/Los_Angeles' }));
      const hours = ptTime.getHours();

      // Check if it's between 7pm and midnight
      if (hours >= 19) {
        const today = format(new Date(), 'yyyy-MM-dd');
        const lastPrompt = localStorage.getItem(LAST_PROMPT_KEY);

        // Only show if we haven't prompted today and no submission exists
        if (lastPrompt !== today && !todaySubmission) {
          setIsOpen(true);
          localStorage.setItem(LAST_PROMPT_KEY, today);

          // Request notification permission and send notification
          if ('Notification' in window && Notification.permission === 'granted') {
            new Notification('Time to submit your daily update', {
              body: 'Please complete your EOD form for today.',
              icon: '/favicon.ico'
            });
          }
        }
      }
    };

    // Request notification permission on mount
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission();
    }

    // Check immediately and then every minute
    checkTime();
    const interval = setInterval(checkTime, 60000);

    return () => clearInterval(interval);
  }, [todaySubmission]);

  const saveDraft = useCallback((data: Partial<EODFormData>) => {
    localStorage.setItem(DRAFT_KEY, JSON.stringify({
      ...data,
      savedAt: new Date().toISOString()
    }));
  }, []);

  const loadDraft = useCallback((): Partial<EODFormData> | null => {
    const stored = localStorage.getItem(DRAFT_KEY);
    if (stored) {
      return JSON.parse(stored);
    }
    return null;
  }, []);

  const clearDraft = useCallback(() => {
    localStorage.removeItem(DRAFT_KEY);
  }, []);

  const submitEOD = useCallback((formData: EODFormData) => {
    const now = new Date();
    const today = format(now, 'yyyy-MM-dd');

    // Check if submitting late (after midnight)
    const ptTime = new Date(now.toLocaleString('en-US', { timeZone: 'America/Los_Angeles' }));
    const isLate = ptTime.getHours() < 6; // Before 6am means late from previous day

    const demosBooked = parseInt(formData.demos_booked) || 0;
    const demosShowed = parseInt(formData.demos_showed) || 0;
    const demosClosed = parseInt(formData.demos_closed) || 0;
    const revenueClosed = parseFloat(formData.revenue_closed) || undefined;
    const showRate = demosBooked > 0 ? Math.round((demosShowed / demosBooked) * 100) : 0;
    const reportDate = isLate ? format(new Date(now.getTime() - 86400000), 'yyyy-MM-dd') : today;

    const submission: EODSubmission = {
      id: crypto.randomUUID(),
      user_id: '', // Will be set from session below
      date: reportDate,
      demos_booked: demosBooked,
      demos_showed: demosShowed,
      demos_closed: demosClosed,
      revenue_closed: revenueClosed,
      show_rate: showRate,
      lead_quality: formData.lead_quality as EODSubmission['lead_quality'],
      lead_quality_issues: formData.lead_quality_issues,
      issues: formData.issues,
      notes: formData.notes,
      tomorrow_focus: formData.tomorrow_focus,
      submitted_at: now.toISOString(),
      submitted_late: isLate
    };

    // Fire-and-forget upsert to Supabase
    (async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session?.user?.id) {
          console.error('No auth session for EOD submit');
          return;
        }

        const userId = session.user.id;
        submission.user_id = userId;

        const { error } = await supabase.from('eod_reports').upsert({
          user_id: userId,
          report_date: reportDate,
          demos_booked: demosBooked,
          demos_showed: demosShowed,
          demos_closed: demosClosed,
          revenue_closed: revenueClosed ? Math.round(revenueClosed * 100) : null, // dollars to cents
          show_rate: showRate,
          lead_quality: formData.lead_quality || null,
          lead_quality_issues: formData.lead_quality_issues || null,
          issues: formData.issues || null,
          notes: formData.notes || null,
          tomorrow_focus: formData.tomorrow_focus || null,
          submitted_at: now.toISOString(),
          submitted_late: isLate,
        }, { onConflict: 'user_id,report_date' });

        if (error) {
          console.error('Error saving EOD report:', error);
        } else {
          // Re-fetch to get server-assigned ID
          fetchSubmissions();
        }
      } catch (err) {
        console.error('EOD submit error:', err);
      }
    })();

    // Optimistic update
    const updated = isEditing
      ? submissions.map(s => s.date === submission.date ? submission : s)
      : [...submissions.filter(s => s.date !== submission.date), submission];

    setSubmissions(updated);
    setTodaySubmission(submission);
    clearDraft();

    return submission;
  }, [submissions, isEditing, clearDraft]);

  const skipToday = useCallback(() => {
    setIsOpen(false);
    localStorage.setItem(LAST_PROMPT_KEY, format(new Date(), 'yyyy-MM-dd'));
  }, []);

  const openForm = useCallback((editing = false) => {
    setIsEditing(editing);
    setIsOpen(true);
  }, []);

  const closeForm = useCallback(() => {
    setIsOpen(false);
    setIsEditing(false);
  }, []);

  const getSubmissionByDate = useCallback((date: string) => {
    return submissions.find(s => s.date === date);
  }, [submissions]);

  return {
    isOpen,
    setIsOpen,
    submissions,
    todaySubmission,
    isEditing,
    submitEOD,
    skipToday,
    openForm,
    closeForm,
    saveDraft,
    loadDraft,
    clearDraft,
    getSubmissionByDate
  };
}
